import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './Workout.css';
import PreviousWorkouts from './PreviousWorkouts';
import { useClient } from '../../context/ClientContext';
import ClientHistoryDisplay from './ClientHistoryDisplay';

const Workout = () => {
  const { workoutClientContext, setWorkoutClientContext } = useClient();
  const { session } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(workoutClientContext);
  const [searchClient, setSearchClient] = useState(
    workoutClientContext ? `${workoutClientContext.first_name} ${workoutClientContext.last_name}` : ''
  );
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [workoutDate, setWorkoutDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [workoutRows, setWorkoutRows] = useState([
    { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' },
    { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' },
    { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' },
  ]);
  const [activeRow, setActiveRow] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [showPRContainer, setShowPRContainer] = useState(false);
  const [prExercise, setPrExercise] = useState('');
  const [prWeight, setPrWeight] = useState('');
  const [prReps, setPrReps] = useState('');
  const [prSearchTerm, setPrSearchTerm] = useState('');
  const [filteredPrExercises, setFilteredPrExercises] = useState([]);
  const [showPrDropdown, setShowPrDropdown] = useState(false);
  const [clientPRs, setClientPRs] = useState([]);
  const [showCopyWorkoutModal, setShowCopyWorkoutModal] = useState(false);
  const [availableWorkouts, setAvailableWorkouts] = useState([]);

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('*');
        if (error) throw error;
        setClients(data || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    // Initial fetch
    fetchClients();

    // Subscribe to changes
    const subscription = supabase
      .channel('clients_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'clients' 
        }, 
        (payload) => {
          // Refetch all clients when any change occurs
          fetchClients();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Add this effect right after the clients fetch effect
  useEffect(() => {
    const initializeWorkout = async () => {
      if (workoutClientContext) {
        console.log('Initializing workout from context:', workoutClientContext);
        
        // Check for incomplete workout
        const { data: incompleteWorkout, error: workoutError } = await supabase
          .from('workouts')
          .select(`
            *,
            workout_exercises (
              id,
              exercise_id,
              sets,
              reps,
              time,
              weight,
              exercises (
                id,
                name,
                muscle_group
              )
            )
          `)
          .eq('client_id', workoutClientContext.id)
          .eq('completed', false)
          .order('workout_date', { ascending: false })
          .limit(1)
          .single();

        if (workoutError && workoutError.code !== 'PGRST116') {
          console.error('Error fetching incomplete workout:', workoutError);
          return;
        }

        if (incompleteWorkout) {
          setWorkoutDate(new Date(incompleteWorkout.workout_date));
          setWorkoutNotes(incompleteWorkout.notes || '');
          
          const exerciseRows = incompleteWorkout.workout_exercises.map(we => ({
            exerciseId: we.exercise_id,
            exerciseName: we.exercises.name,
            muscleGroup: we.exercises.muscle_group,
            sets: we.sets || '',
            reps: we.reps || '',
            time: we.time || '',
            weight: we.weight || ''
          }));

          while (exerciseRows.length < 3) {
            exerciseRows.push({ 
              exerciseId: '', 
              exerciseName: '', 
              muscleGroup: '', 
              sets: '', 
              reps: '', 
              time: '',
              weight: '' 
            });
          }

          setWorkoutRows(exerciseRows);
        }
      }
    };

    initializeWorkout();
  }, [workoutClientContext]); // Only depend on workoutClientContext

  const handleSearch = async (searchQuery) => {
    try {
      // Split the search query into words
      const searchTerms = searchQuery.trim().split(/\s+/);
      
      let query = supabase
        .from('clients')
        .select('*');

      if (searchTerms.length === 1) {
        // If single word, search in both first and last name
        query = query.or(`first_name.ilike.%${searchTerms[0]}%,last_name.ilike.%${searchTerms[0]}%`);
      } else if (searchTerms.length > 1) {
        // If multiple words, assume first word is first name and last word is last name
        const firstName = searchTerms[0];
        const lastName = searchTerms[searchTerms.length - 1];
        query = query
          .ilike('first_name', `%${firstName}%`)
          .ilike('last_name', `%${lastName}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error searching clients:', error);
    }
  };

  const handleClientSelect = useCallback(async (client) => {
    console.log('1. handleClientSelect called with client:', client);
    setSelectedClient(client);
    setWorkoutClientContext(client);
    setSearchClient(`${client.first_name} ${client.last_name}`);
    setShowClientDropdown(false);

    const fetchClientPRs = async (clientId) => {
      const { data, error } = await supabase
        .from('personal_records')
        .select(`
          *,
          exercises (
            name
          )
        `)
        .eq('client_id', clientId)
        .order('achieved_date', { ascending: false })
        .limit(6);

      if (!error && data) {
        const prs = data.map(pr => ({
          exerciseName: pr.exercises.name,
          weight: pr.weight,
          reps: pr.reps,
          date: new Date(pr.achieved_date).toLocaleDateString()
        }));
        setClientPRs(prs);
      }
    };

    await fetchClientPRs(client.id);

    // Check for incomplete workout
    console.log('2. Fetching incomplete workouts for client ID:', client.id);
    const { data: incompleteWorkout, error: workoutError } = await supabase
      .from('workouts')
      .select(`
        *,
        workout_exercises (
          id,
          exercise_id,
          sets,
          reps,
          time,
          weight,
          exercises (
            id,
            name,
            muscle_group
          )
        )
      `)
      .eq('client_id', client.id)
      .eq('completed', false)
      .order('workout_date', { ascending: false })
      .limit(1)
      .single();

    console.log('3. Incomplete workout query result:', { incompleteWorkout, workoutError });

    if (workoutError && workoutError.code !== 'PGRST116') {
      console.error('4. Error fetching incomplete workout:', workoutError);
      return;
    }

    if (incompleteWorkout) {
      console.log('5. Found incomplete workout, populating form:', incompleteWorkout);
      setWorkoutDate(new Date(incompleteWorkout.workout_date));
      setWorkoutNotes(incompleteWorkout.notes || '');
      
      const exerciseRows = incompleteWorkout.workout_exercises.map(we => ({
        exerciseId: we.exercise_id,
        exerciseName: we.exercises.name,
        muscleGroup: we.exercises.muscle_group,
        sets: we.sets || '',
        reps: we.reps || '',
        time: we.time || '',
        weight: we.weight || ''
      }));

      console.log('6. Mapped exercise rows:', exerciseRows);

      while (exerciseRows.length < 3) {
        exerciseRows.push({ 
          exerciseId: '', 
          exerciseName: '', 
          muscleGroup: '', 
          sets: '', 
          reps: '', 
          time: '',
          weight: '' 
        });
      }

      setWorkoutRows(exerciseRows);
    } else {
      console.log('7. No incomplete workout found, resetting form');
      setWorkoutDate(new Date());
      setWorkoutNotes('');
      setWorkoutRows([
        { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' },
        { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' },
        { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' }
      ]);
    }
  }, [setWorkoutClientContext, setWorkoutDate, setWorkoutNotes, setWorkoutRows, setClientPRs]);

  // Update the initialization effect with proper dependencies
  useEffect(() => {
    const initializeClientWorkout = async () => {
      if (workoutClientContext && !selectedClient) {
        console.log('Initializing workout from context on mount');
        await handleClientSelect(workoutClientContext);
      } else if (workoutClientContext && selectedClient && clientPRs.length === 0) {
        // If coming back to tab with selected client but no PRs, fetch the PRs
        const fetchClientPRs = async (clientId) => {
          const { data, error } = await supabase
            .from('personal_records')
            .select(`
              *,
              exercises (
                name
              )
            `)
            .eq('client_id', clientId)
            .order('achieved_date', { ascending: false })
            .limit(6);

          if (!error && data) {
            const prs = data.map(pr => ({
              exerciseName: pr.exercises.name,
              weight: pr.weight,
              reps: pr.reps,
              date: new Date(pr.achieved_date).toLocaleDateString()
            }));
            setClientPRs(prs);
          }
        };

        await fetchClientPRs(workoutClientContext.id);
      }
    };
    
    initializeClientWorkout();
  }, [workoutClientContext, selectedClient, handleClientSelect, clientPRs.length]);

  // Handle exercise search server-side
  const handleExerciseSearch = async (searchQuery) => {
    try {
      if (!searchQuery.trim()) {
        setFilteredExercises([]);
        return;
      }

      const searchLower = searchQuery.toLowerCase();
      
      const { data, error } = await supabase
        .from('exercises')
        .select('*')
        .or(`name.ilike.%${searchLower}%,muscle_group.ilike.%${searchLower}%`)
        .order('name');

      if (error) throw error;
      setFilteredExercises(data || []);
    } catch (error) {
      console.error('Error searching exercises:', error);
      setFilteredExercises([]);
    }
  };

  // Handle PR exercise search server-side
  const handlePRExerciseSearch = async (searchQuery) => {
    try {
      if (!searchQuery.trim()) {
        setFilteredPrExercises([]);
        return;
      }

      const searchLower = searchQuery.toLowerCase();
      
      const { data, error } = await supabase
        .from('exercises')
        .select('*')
        .ilike('name', `%${searchLower}%`)
        .order('name');

      if (error) throw error;
      setFilteredPrExercises(data || []);
    } catch (error) {
      console.error('Error searching PR exercises:', error);
      setFilteredPrExercises([]);
    }
  };

  const fetchLastExerciseData = async (clientId, exerciseId) => {
    const { data, error } = await supabase
      .from('workout_exercises')
      .select(`
        sets,
        reps,
        weight,
        time,
        workout_id,
        workouts!inner(
          client_id
        )
      `)
      .eq('workouts.client_id', clientId)
      .eq('exercise_id', exerciseId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error fetching last exercise data:', error);
      return null;
    }

    return data?.[0] || null;
  };

  const handleExerciseSelect = async (exercise, rowIndex) => {
    if (!selectedClient) return;

    // First update the basic exercise info
    const newRows = [...workoutRows];
    
    // Fetch last exercise data for this client and exercise
    const lastData = await fetchLastExerciseData(selectedClient.id, exercise.id);

    newRows[rowIndex] = {
      ...newRows[rowIndex],
      exerciseId: exercise.id,
      exerciseName: exercise.name,
      muscleGroup: exercise.muscle_group,
      // Only populate these fields if we have previous data
      sets: lastData?.sets || '',
      reps: lastData?.reps || '',
      weight: lastData?.weight || '',
      time: lastData?.time || ''
    };

    setWorkoutRows(newRows);
    setActiveRow(null);
    setSearchTerm('');
  };

  const handleInputChange = (rowIndex, field, value) => {
    const newRows = [...workoutRows];
    newRows[rowIndex] = {
      ...newRows[rowIndex],
      [field]: value,
    };
    setWorkoutRows(newRows);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedClient || !session) {
        alert('Please select a client');
        return;
      }

      const validRows = workoutRows.filter(row => row.exerciseName.trim());
      if (validRows.length === 0) {
        alert('Please add at least one exercise');
        return;
      }

      // Check if we're working with an existing workout
      const { data: existingWorkout } = await supabase
        .from('workouts')
        .select('id')
        .eq('client_id', selectedClient.id)
        .eq('completed', false)
        .single();

      let workoutId;

      if (existingWorkout) {
        // Update existing workout
        const { error } = await supabase
          .from('workouts')
          .update({
            workout_date: workoutDate.toISOString(),
            notes: workoutNotes
          })
          .eq('id', existingWorkout.id)
          .select()
          .single();

        if (error) throw error;
        workoutId = existingWorkout.id;

        // Update exercises
        const { error: deleteError } = await supabase
          .from('workout_exercises')
          .delete()
          .eq('workout_id', workoutId);

        if (deleteError) throw deleteError;

        const { error: insertError } = await supabase
          .from('workout_exercises')
          .insert(validRows.map(row => ({
            workout_id: workoutId,
            exercise_id: row.exerciseId,
            sets: row.sets,
            reps: row.reps,
            time: row.time,
            weight: row.weight
          })));

        if (insertError) throw insertError;

      } else {
        // Create new workout
        const { data, error } = await supabase
          .rpc('handle_workout_submission', {
            p_client_id: selectedClient.id,
            p_trainer_id: session.user.id,
            p_workout_date: workoutDate.toISOString(),
            p_notes: workoutNotes,
            p_workout_exercises: validRows.map(row => ({
              exercise_id: row.exerciseId,
              sets: row.sets,
              reps: row.reps,
              time: row.time,
              weight: row.weight
            }))
          });

        if (error) throw error;
        workoutId = data;
      }

      alert('Success! Workout has been saved.');

    } catch (error) {
      console.error('Error:', error);
      alert('Error saving workout: ' + error.message);
    }
  };

  const addExerciseRow = () => {
    setWorkoutRows([
      ...workoutRows,
      { exerciseId: '', exerciseName: '', sets: '', reps: '', time: '', weight: '', muscleGroup: '' }
    ]);
  };

  const handleComplete = async () => {
    if (!selectedClient || !session) {
      alert('Please select a client');
      return;
    }

    const validRows = workoutRows.filter(row => row.exerciseName.trim());
    if (validRows.length === 0) {
      alert('Please add at least one exercise');
      return;
    }

    try {
      // Call stored procedure
      const { data: workoutId, error } = await supabase
        .rpc('complete_workout', {
          p_client_id: selectedClient.id,
          p_workout_date: workoutDate.toISOString(),
          p_notes: workoutNotes,
          p_workout_exercises: validRows.map(row => ({
            exercise_id: row.exerciseId,
            sets: row.sets,
            reps: row.reps,
            time: row.time,
            weight: row.weight
          }))
        });

      if (error) {
        console.error('Transaction error:', error);
        throw new Error(error.message || 'Failed to complete workout');
      }

      if (!workoutId) {
        throw new Error('No workout ID returned from server');
      }

      setIsCompleted(true);
      resetForm();
      alert('Workout completed successfully!');

    } catch (error) {
      console.error('Error completing workout:', error);
      alert('Error completing workout: ' + error.message);
    }
  };

  // Update the resetForm function to also reset workoutNotes
  const resetForm = () => {
    setSelectedClient(null);
    setSearchClient('');
    setWorkoutDate(new Date());
    setWorkoutRows([
      { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' },
      { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' },
      { exerciseId: '', exerciseName: '', muscleGroup: '', sets: '', reps: '', time: '', weight: '' }
    ]);
    setIsCompleted(false);
    setWorkoutNotes('');
  };

  // Add this new function to handle saving PRs
  const handleSavePR = async () => {
    if (!selectedClient || !prExercise || !prWeight || !prReps) {
      alert('Please fill in all PR fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('personal_records')
        .insert({
          client_id: selectedClient.id,
          exercise_id: prExercise,
          weight: parseFloat(prWeight),
          reps: parseInt(prReps),
          verified_by: session.user.id,
          achieved_date: new Date().toISOString()
        });

      if (error) throw error;

      // Clear the PR form
      setPrExercise('');
      setPrWeight('');
      setPrReps('');
      setShowPRContainer(false);
      
      alert('PR saved successfully!');
    } catch (error) {
      console.error('Error saving PR:', error);
      alert('Error saving PR: ' + error.message);
    }
  };

  // Add this function to fetch available workouts for copying
  const fetchAvailableWorkouts = async () => {
    try {
      const { data, error } = await supabase
        .from('workouts')
        .select(`
          id,
          workout_date,
          client_id,
          clients (
            first_name,
            last_name
          ),
          workout_exercises (
            exercise_id,
            sets,
            reps,
            time,
            weight,
            exercises (
              id,
              name,
              muscle_group
            )
          )
        `)
        .eq('completed', true)
        .eq('trainer_id', session.user.id)
        .order('workout_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      setAvailableWorkouts(data || []);
    } catch (error) {
      console.error('Error fetching available workouts:', error);
    }
  };

  // Function to handle opening the copy workout modal
  const handleOpenCopyWorkout = () => {
    fetchAvailableWorkouts();
    setShowCopyWorkoutModal(true);
  };

  // Function to handle copying a workout
  const handleCopyWorkout = (workout) => {
    if (!workout.workout_exercises || workout.workout_exercises.length === 0) {
      alert('Selected workout has no exercises to copy');
      return;
    }

    const newRows = workout.workout_exercises.map(exercise => ({
      exerciseId: exercise.exercise_id,
      exerciseName: exercise.exercises.name,
      muscleGroup: exercise.exercises.muscle_group,
      sets: exercise.sets || '',
      reps: exercise.reps || '',
      time: exercise.time || '',
      weight: exercise.weight || ''
    }));

    setWorkoutRows(newRows);
    setShowCopyWorkoutModal(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="workout-form">
        <div className="workout-header">
          <div className="client-search">
            <input
              type="text"
              value={searchClient}
              onChange={(e) => {
                const value = e.target.value;
                setSearchClient(value);
                setShowClientDropdown(true);
                handleSearch(value);
              }}
              placeholder="Search client..."
              className="client-search-input"
            />
            {showClientDropdown && searchClient && (
              <div className="client-dropdown">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className="client-option"
                    onClick={() => handleClientSelect(client)}
                  >
                    {client.first_name} {client.last_name} - {client.email}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="date-picker-container">
            <DatePicker
              selected={workoutDate}
              onChange={(date) => setWorkoutDate(date)}
              className="date-picker"
            />
          </div>
        </div>

        {selectedClient && (
          <div className="client-info">
            <div className="client-details">
              <h3>Selected Client: {selectedClient.first_name} {selectedClient.last_name}</h3>
              <p>Goals: {selectedClient.goals || 'None'}</p>
              <p>Limitations: {selectedClient.injuries || 'None'}</p>
              <ClientHistoryDisplay selectedClient={selectedClient} />
            </div>
            <div className="client-prs">
              <h3>Recent Personal Records</h3>
              <div className="pr-list">
                {clientPRs.map((pr, index) => (
                  <div key={index} className="pr-item">
                    <span className="pr-label">{pr.exerciseName}:</span>
                    <span className="pr-value">
                      {`${pr.weight}lbs × ${pr.reps} (${pr.date})`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="workout-table">
          <div className="workout-row header">
            <div className="col exercise">Exercise Name</div>
            <div className="col sets">Sets</div>
            <div className="col reps">Reps</div>
            <div className="col weight">Weight</div>
            <div className="col time">Time</div>
            <div className="col muscle-group">Muscle Group</div>
          </div>

          {workoutRows.map((row, index) => (
            <div key={index} className="workout-row">
              <div className="col exercise">
                <input
                  type="text"
                  value={row.exerciseName}
                  onChange={(e) => {
                    handleInputChange(index, 'exerciseName', e.target.value);
                    setSearchTerm(e.target.value);
                    setActiveRow(index);
                    handleExerciseSearch(e.target.value);
                  }}
                  onFocus={() => {
                    setActiveRow(index);
                    setSearchTerm(row.exerciseName);
                    if (row.exerciseName) {
                      handleExerciseSearch(row.exerciseName);
                    }
                  }}
                  placeholder="Search by exercise or muscle group..."
                />
                {activeRow === index && searchTerm && (
                  <div className="exercise-dropdown">
                    {filteredExercises.map((exercise) => (
                      <div
                        key={exercise.id}
                        className="exercise-option"
                        onClick={() => handleExerciseSelect(exercise, index)}
                      >
                        {exercise.name} ({exercise.muscle_group})
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="col sets">
                <input
                  type="text"
                  value={row.sets}
                  onChange={(e) => handleInputChange(index, 'sets', e.target.value)}
                  placeholder="Sets"
                />
              </div>
              
              <div className="col reps">
                <input
                  type="text"
                  value={row.reps}
                  onChange={(e) => handleInputChange(index, 'reps', e.target.value)}
                  placeholder="Reps"
                />
              </div>
              
              <div className="col weight">
                <input
                  type="text"
                  value={row.weight}
                  onChange={(e) => handleInputChange(index, 'weight', e.target.value)}
                  placeholder="Weight"
                />
              </div>
              
              <div className="col time">
                <input
                  type="text"
                  value={row.time}
                  onChange={(e) => handleInputChange(index, 'time', e.target.value)}
                  placeholder="Time"
                />
              </div>

              <div className="col muscle-group">
                <div className="muscle-group-display">{row.muscleGroup}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="workout-notes">
          <h4>Workout Notes</h4>
          <textarea
            value={workoutNotes}
            onChange={(e) => setWorkoutNotes(e.target.value)}
            placeholder="Enter any additional notes about the workout..."
            rows={4}
            className="notes-textarea"
          />
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={addExerciseRow} 
            className="add-exercise-btn"
          >
            + Add Exercise
          </button>
          <button 
            type="button"
            onClick={() => setShowPRContainer(!showPRContainer)}
            className="add-pr-btn"
          >
           + Add PR
          </button>
          {selectedClient && (
            <button
              type="button"
              onClick={handleOpenCopyWorkout}
              className="copy-workout-btn"
            >
              Copy Workout
            </button>
          )}
          <button 
            type="button"
            onClick={handleComplete}
            className="complete-btn"
            disabled={isCompleted || !selectedClient}
          >
            {isCompleted ? 'Completed' : 'Mark Complete'}
          </button>
          <button 
            type="submit" 
            className="workout-submit-btn"
            disabled={isCompleted}
          >
            Save Workout
          </button>
        </div>

        {showPRContainer && (
          <div className="pr-container">
            <h4>Personal Record</h4>
            <div className="pr-row">
              <div className="col exercise">
                <input
                  type="text"
                  value={prSearchTerm}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrSearchTerm(v);
                    setShowPrDropdown(!!v);        // open if there's text
                    handlePRExerciseSearch(v);
                  }}
                  onFocus={() => {
                    if (prSearchTerm) setShowPrDropdown(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowPrDropdown(false), 100);
                  }}
                  placeholder="Search exercise..."
                  className="pr-exercise-dropdown"
                />
                {showPrDropdown && filteredPrExercises.length > 0 && (
                  <div className="exercise-dropdown">
                    {filteredPrExercises.map((exercise) => (
                      <div
                        key={exercise.id}
                        className="exercise-option"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setPrExercise(exercise.id);
                          setPrSearchTerm(`${exercise.name} (${exercise.muscle_group})`);
                          setFilteredPrExercises([]);
                          setShowPrDropdown(false);
                        }}
                      >
                        {exercise.name} ({exercise.muscle_group})
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="text"
                value={prWeight}
                onChange={(e) => setPrWeight(e.target.value)}
                placeholder="Weight"
                className="pr-weight-input"
              />
              <input
                type="text"
                value={prReps}
                onChange={(e) => setPrReps(e.target.value)}
                placeholder="Reps"
                className="pr-reps-input"
              />
              <button
                type="button"
                onClick={handleSavePR}
                className="workout-submit-btn"
              >
                Save PR
              </button>
            </div>
          </div>
        )}
      </form>
      
      {showCopyWorkoutModal && (
        <div className="modal-overlay">
          <div className="copy-workout-modal">
            <div className="modal-header">
              <h3>Select Workout to Copy</h3>
              <button
                type="button"
                className="close-modal-btn"
                onClick={() => setShowCopyWorkoutModal(false)}
              >
                ×
              </button>
            </div>
            <div className="available-workouts">
              {availableWorkouts.map((workout) => (
                <div key={workout.id} className="available-workout-item">
                  <div className="workout-summary">
                    <div className="workout-info">
                      <span className="workout-client">
                        {workout.clients.first_name} {workout.clients.last_name}
                      </span>
                      <span className="workout-date">
                        {new Date(workout.workout_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="workout-exercises-summary">
                      {workout.workout_exercises.slice(0, 3).map((exercise, idx) => (
                        <span key={idx} className="summary-exercise">
                          {exercise.exercises.name}
                        </span>
                      ))}
                      {workout.workout_exercises.length > 3 && 
                        <span className="more-indicator">+{workout.workout_exercises.length - 3} more</span>
                      }
                    </div>
                  </div>
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() => handleCopyWorkout(workout)}
                  >
                    Copy
                  </button>
                </div>
              ))}
              {availableWorkouts.length === 0 && (
                <div className="no-workouts">No workouts available to copy</div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <PreviousWorkouts selectedClient={selectedClient} />
    </>
  );
};

export default Workout; 